import { Injectable } from '@angular/core';
import { CmsService } from './cms.service';
import { SiteSettingsMap } from './cms.models';

/**
 * Shared accessor for Site Settings (contact details, social links, hours, map)
 * so headers/footer/contact all read the same admin-managed values instead of
 * hardcoding them. Backed by CmsService.getSettings() (cached), loaded once.
 */
@Injectable({ providedIn: 'root' })
export class SiteInfoService {
  s: SiteSettingsMap = {};

  constructor(private cms: CmsService) {
    this.cms.getSettings().subscribe({
      next: (m) => { this.s = m || {}; },
      error: () => { /* keep static fallbacks if settings can't load */ }
    });
  }

  /** Raw setting value, or the fallback when missing/empty. */
  get(key: string, fallback = ''): string {
    const v = this.s[key];
    return v && v.trim().length ? v : fallback;
  }

  /** Social URL for a network (facebook/twitter/linkedin/instagram); '' if unset. */
  social(network: string): string { return this.get(`social.${network}`); }

  email(fallback = ''): string { return this.get('contact.email', fallback); }
  phone(fallback = ''): string { return this.get('contact.phone', fallback); }

  /** Localized value with EN<->AR fallback for a `<base>En`/`<base>Ar` pair. */
  loc(base: string, isArabic: boolean, fallback = ''): string {
    const ar = this.s[`${base}Ar`];
    const en = this.s[`${base}En`];
    if (isArabic) return (ar && ar.trim() ? ar : en) || fallback;
    return (en && en.trim() ? en : ar) || fallback;
  }

  address(isArabic: boolean, fallback = ''): string { return this.loc('contact.address', isArabic, fallback); }
  hours(isArabic: boolean, fallback = ''): string { return this.loc('contact.hours', isArabic, fallback); }

  /** True if at least one social link is configured. */
  get hasSocial(): boolean {
    return ['facebook', 'twitter', 'linkedin', 'instagram'].some((n) => !!this.social(n));
  }
}
